import { useEffect, useState } from "react";
import { api, errMsg, R_EVENTO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { MonthCalendar } from "@/components/MonthCalendar";
import { ChevronLeft, ChevronRight, Loader2, MailWarning, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  area_id: "", data: "", inicio: "", fim: "",
  eql_id: null, eql_label: "", quadra: "", lote: "", morador_id: null,
  solicitante_nome: "", solicitante_email: "", solicitante_telefone: "",
  evento_tipo: "outro", convidados: 0, notas: "", termos: false,
};
const STEPS = ["Espaço e data", "Morador", "Evento e termos"];
const EDITABLE = ["area_id", "data", "inicio", "fim", "eql_id", "eql_label", "quadra", "lote",
  "morador_id", "solicitante_nome", "solicitante_email", "solicitante_telefone",
  "evento_tipo", "convidados", "notas"];

const moradorPhone = (m) => [m.ddi_celular, m.celular].filter(Boolean).join(" ") || m.telefone || "";
export const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
export const fmtDataBR = (ds) => (ds ? ds.split("-").reverse().join("/") : "—");

export const waNumber = (tel) => {
  const d = (tel || "").replace(/\D/g, "");
  const local = d.startsWith("55") && d.length > 11 ? d.slice(2) : d;
  if (!/^[1-9]{2}(9?\d{8})$/.test(local)) return null;
  return `55${local}`;
};

export default function ReservaFormDialog({ open, onOpenChange, editing, areas, onSaved }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [cal, setCal] = useState(() => { const n = new Date(); return { ano: n.getFullYear(), mes: n.getMonth() }; });
  const [disp, setDisp] = useState(null);
  const [eqlOpen, setEqlOpen] = useState(false);
  const [moradores, setMoradores] = useState(null);
  const [diag, setDiag] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setMoradores(null);
    if (editing) {
      const f = { ...EMPTY, termos: true };
      for (const k of EDITABLE) f[k] = editing[k] ?? EMPTY[k];
      setForm(f);
      const [y, m] = (editing.data || "").split("-").map(Number);
      if (y && m) setCal({ ano: y, mes: m - 1 });
    } else {
      setForm(EMPTY);
      const n = new Date();
      setCal({ ano: n.getFullYear(), mes: n.getMonth() });
    }
  }, [open, editing]);

  useEffect(() => {
    if (!open || !form.area_id) { setDisp(null); return; }
    const mes = `${cal.ano}-${String(cal.mes + 1).padStart(2, "0")}`;
    api.get("/tenant/reservas/disponibilidade", { params: { area_id: form.area_id, mes } })
      .then(({ data }) => setDisp(data))
      .catch((err) => { setDisp(null); toast.error(errMsg(err)); });
  }, [open, form.area_id, cal]);

  const navigate = (delta) => setCal((c) => {
    const d = new Date(c.ano, c.mes + delta, 1);
    return { ano: d.getFullYear(), mes: d.getMonth() };
  });

  const dayState = (ds) => {
    const d = disp?.dias?.[ds];
    if (!d) return "vazio";
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    if (ds < today) return "vazio";
    if (d.bloqueado) return "bloqueado";
    if (!d.periodos?.length) return "vazio";
    return d.reservas?.length ? "ocupado" : "livre";
  };

  const dayInfo = form.data ? disp?.dias?.[form.data] : null;

  const selectEql = async (eql) => {
    const label = [eql.numero, eql.descricao].filter(Boolean).join(" — ") || eql.id;
    const code = (eql.descricao || "").trim();
    const derived = code.length === 6
      ? { quadra: code.slice(2, 4).toUpperCase(), lote: code.slice(4, 6).toUpperCase() }
      : { quadra: eql.quadra || "", lote: eql.lote || "" };
    setForm((f) => ({ ...f, eql_id: eql.id, eql_label: label, ...derived, morador_id: null }));
    setEqlOpen(false);
    setMoradores(null);
    try {
      const { data } = await api.get("/tenant/brave/moradores", { params: { eql: eql.descricao || eql.numero || eql.id } });
      const lista = data.moradores || [];
      setMoradores(lista);
      if (!lista.length) {
        setDiag({
          titulo: "Nenhum morador retornado para a EQL",
          texto: JSON.stringify({ endpoint: data.debug?.endpoint, params: data.debug?.params, total: data.debug?.total, count: data.debug?.count }, null, 2),
        });
      }
    } catch (err) {
      setMoradores([]);
      setDiag({ titulo: "Falha ao consultar moradores na API Brave Access", texto: errMsg(err) });
    }
  };

  const selectMorador = (id) => {
    const m = (moradores || []).find((x) => x.id === id);
    if (!m) return;
    setForm((f) => ({
      ...f, morador_id: m.id,
      solicitante_nome: m.nome || f.solicitante_nome,
      solicitante_email: m.email || f.solicitante_email,
      solicitante_telefone: moradorPhone(m) || f.solicitante_telefone,
    }));
  };

  const next = () => {
    if (step === 1 && (!form.area_id || !form.data || !form.inicio || !form.fim)) {
      toast.error("Selecione o espaço, a data e o horário da reserva");
      return;
    }
    if (step === 1 && form.fim <= form.inicio) {
      toast.error("O horário final deve ser maior que o inicial");
      return;
    }
    if (step === 1) {
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      if (form.data === today) {
        const [h, m] = form.inicio.split(":").map(Number);
        const inicioDt = new Date(now);
        inicioDt.setHours(h, m, 0, 0);
        const minAnt = disp?.antecedencia_min || 0;
        if (inicioDt.getTime() < now.getTime() + minAnt * 60000) {
          toast.error(minAnt > 0
            ? `Horário inicial indisponível — já passou ou não atende a antecedência mínima de ${minAnt} min`
            : "O horário inicial já passou — selecione um horário futuro");
          return;
        }
      }
      const conflito = (dayInfo?.reservas || []).find((r) =>
        r.numero !== editing?.numero && form.inicio < r.fim && form.fim > r.inicio);
      if (conflito) {
        toast.error(`Horário já reservado — conflito com a reserva #${String(conflito.numero).padStart(4, "0")} (${conflito.inicio}–${conflito.fim})`);
        return;
      }
      const periodos = dayInfo?.periodos || [];
      if (periodos.length && !periodos.some((p) => form.inicio >= p[0] && form.fim <= p[1])) {
        toast.error(`Horário fora da disponibilidade do espaço neste dia (${periodos.map((p) => `${p[0]}–${p[1]}`).join(" / ")})`);
        return;
      }
    }
    if (step === 2 && !form.solicitante_nome.trim()) {
      toast.error("Informe o nome do solicitante");
      return;
    }
    setStep(step + 1);
  };

  const submit = async () => {
    if (!editing && !form.termos) {
      toast.error("É obrigatório aceitar os termos de uso do espaço");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const norm = (v) => (v === undefined || v === null ? "" : v);
        const diff = {};
        for (const k of EDITABLE) {
          if (norm(form[k]) !== norm(editing[k])) diff[k] = form[k] === "" ? null : form[k];
        }
        if (!Object.keys(diff).length) {
          toast.info("Nenhuma alteração para salvar");
          onOpenChange(false);
          return;
        }
        await api.patch(`/tenant/reservas/${editing.id}`, diff);
        toast.success("Reserva atualizada");
      } else {
        await api.post("/tenant/reservas", { ...form, convidados: Number(form.convidados) || 0 });
        toast.success("Reserva confirmada");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const openWhatsApp = () => {
    const num = waNumber(form.solicitante_telefone);
    if (!num) return;
    const msg = encodeURIComponent(
      `Olá ${form.solicitante_nome || ""}! Confirmação de reserva: ${area ? `${area.code} — ${area.name}` : "espaço"} em ${fmtDataBR(form.data)}, das ${form.inicio} às ${form.fim}.`);
    const w = 420;
    const h = 720;
    const left = Math.max(0, window.screen.availWidth - w - 24);
    window.open(`https://wa.me/${num}?text=${msg}`, "helpdesk-whatsapp",
      `popup=yes,width=${w},height=${h},left=${left},top=60`);
  };

  const area = areas.find((a) => a.id === form.area_id);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="reserva-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">{editing ? `Editar Reserva #${String(editing.numero).padStart(4, "0")}` : "Nova Reserva"}</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              {editing ? "Ajuste os dados da reserva. Alterações de data/horário são revalidadas." : "A reserva nasce confirmada após a conclusão das 3 etapas."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 mb-4" data-testid="reserva-steps">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-2 flex-1" data-testid={`reserva-step-${i + 1}`}>
                <span className={`h-6 w-6 rounded-full text-[11px] flex items-center justify-center font-medium shrink-0 ${step > i ? "bg-indigo-600 text-white" : "bg-zinc-800 text-zinc-500"}`}>{i + 1}</span>
                <span className={`text-xs truncate ${step > i ? "text-zinc-100" : "text-zinc-500"}`}>{s}</span>
                {i < STEPS.length - 1 && <span className="flex-1 h-px bg-zinc-800" />}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-4" data-testid="reserva-step1-panel">
              <div>
                <Label className="text-xs text-zinc-400">Espaço (Áreas e Locais) *</Label>
                <Select value={form.area_id} onValueChange={(v) => setForm({ ...form, area_id: v, data: "", inicio: "", fim: "" })}>
                  <SelectTrigger data-testid="reserva-area-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md">
                    <SelectValue placeholder="Selecione o espaço…" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    {areas.map((a) => (
                      <SelectItem key={a.id} data-testid={`reserva-area-${a.id}`} value={a.id}>{a.code} — {a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.area_id && (
                <>
                  <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                    <MonthCalendar ano={cal.ano} mes={cal.mes} onNavigate={navigate} dayState={dayState}
                      onSelect={(ds) => setForm((f) => ({ ...f, data: ds }))} selected={form.data} />
                  </div>
                  {disp && (
                    <p className="text-[11px] text-zinc-500" data-testid="reserva-area-info">
                      Antecedência mínima: {disp.antecedencia_min} min · Taxa de uso: {fmtBRL(disp.taxa_uso)}
                    </p>
                  )}
                  {dayInfo && (
                    <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 space-y-3" data-testid="reserva-day-info">
                      <p className="text-xs text-zinc-400">
                        {fmtDataBR(form.data)} — disponibilidade:{" "}
                        <span className="text-zinc-200">{dayInfo.periodos.map((p) => `${p[0]}–${p[1]}`).join(" / ") || "—"}</span>
                      </p>
                      {dayInfo.reservas?.length > 0 && (
                        <p className="text-[11px] text-amber-400/90" data-testid="reserva-day-conflitos">
                          Reservas neste dia: {dayInfo.reservas.map((r) => `#${String(r.numero).padStart(4, "0")} ${r.inicio}–${r.fim}`).join(" · ")}
                        </p>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-zinc-400">Início *</Label>
                          <Input type="time" data-testid="reserva-time-inicio" value={form.inicio}
                            onChange={(e) => setForm({ ...form, inicio: e.target.value })}
                            className="mt-1.5 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md [color-scheme:dark]" />
                        </div>
                        <div>
                          <Label className="text-xs text-zinc-400">Fim *</Label>
                          <Input type="time" data-testid="reserva-time-fim" value={form.fim}
                            onChange={(e) => setForm({ ...form, fim: e.target.value })}
                            className="mt-1.5 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md [color-scheme:dark]" />
                        </div>
                      </div>
                      {form.inicio && form.fim && form.inicio < form.fim && dayInfo.periodos?.length > 0
                        && !dayInfo.periodos.some((p) => form.inicio >= p[0] && form.fim <= p[1]) && (
                        <p data-testid="reserva-fora-periodo" className="text-[11px] text-amber-400/90">
                          Horário indisponível — o espaço tem intervalo neste período. Disponível neste dia: {dayInfo.periodos.map((p) => `${p[0]}–${p[1]}`).join(" / ")}.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="reserva-step2-panel">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">EQL (unidade)</Label>
                  <button type="button" data-testid="reserva-eql-button" onClick={() => setEqlOpen(true)}
                    className="mt-1.5 w-full h-9 px-3 text-sm text-left bg-zinc-950 border border-zinc-800 rounded-md hover:border-indigo-500/60 transition-colors duration-200">
                    <span className={form.eql_label ? "text-zinc-100" : "text-zinc-500"}>{form.eql_label || "Selecionar EQL…"}</span>
                  </button>
                </div>
                {moradores && moradores.length > 0 && (
                  <div>
                    <Label className="text-xs text-zinc-400">Morador (base Brave Access)</Label>
                    <Select value={form.morador_id || ""} onValueChange={selectMorador}>
                      <SelectTrigger data-testid="reserva-morador-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md">
                        <SelectValue placeholder="Selecione o morador…" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                        {moradores.map((m) => (
                          <SelectItem key={m.id} data-testid={`reserva-morador-${m.id}`} value={m.id}>{m.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Quadra</Label>
                  <Input data-testid="reserva-quadra-input" value={form.quadra} maxLength={40}
                    onChange={(e) => setForm({ ...form, quadra: e.target.value })}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Lote</Label>
                  <Input data-testid="reserva-lote-input" value={form.lote} maxLength={40}
                    onChange={(e) => setForm({ ...form, lote: e.target.value })}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
                </div>
              </div>
              <p className="text-[11px] text-zinc-500">O solicitante pode ser diferente do morador — ajuste os campos abaixo se necessário.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Nome do solicitante *</Label>
                  <Input data-testid="reserva-nome-input" value={form.solicitante_nome} maxLength={120}
                    onChange={(e) => setForm({ ...form, solicitante_nome: e.target.value })}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">E-mail</Label>
                  <Input data-testid="reserva-email-input" type="email" value={form.solicitante_email} maxLength={200}
                    onChange={(e) => setForm({ ...form, solicitante_email: e.target.value })}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Telefone</Label>
                  <div className="flex items-start gap-1.5">
                    <Input data-testid="reserva-telefone-input" value={form.solicitante_telefone} maxLength={40}
                      onChange={(e) => setForm({ ...form, solicitante_telefone: e.target.value })}
                      className="mt-1.5 flex-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
                    {waNumber(form.solicitante_telefone) && (
                      <Button type="button" data-testid="reserva-whatsapp-button" title="Enviar confirmação via WhatsApp"
                        onClick={openWhatsApp}
                        className="mt-1.5 h-9 w-9 p-0 shrink-0 bg-emerald-600 hover:bg-emerald-500 text-white rounded-md transition-colors duration-200">
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              {!form.solicitante_email.trim() && (
                <p data-testid="reserva-sem-email-aviso" className="flex items-center gap-1.5 text-[11px] text-amber-400/90">
                  <MailWarning className="h-3.5 w-3.5 shrink-0" />
                  Sem e-mail cadastrado — a confirmação da reserva não será enviada por e-mail.
                </p>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="reserva-step3-panel">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Tipo de evento</Label>
                  <Select value={form.evento_tipo} onValueChange={(v) => setForm({ ...form, evento_tipo: v })}>
                    <SelectTrigger data-testid="reserva-evento-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      {Object.entries(R_EVENTO).map(([k, label]) => (
                        <SelectItem key={k} data-testid={`reserva-evento-${k}`} value={k}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Nº de convidados</Label>
                  <Input data-testid="reserva-convidados-input" type="number" min={0} max={2000} value={form.convidados}
                    onChange={(e) => setForm({ ...form, convidados: e.target.value })}
                    className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Observações</Label>
                <Textarea data-testid="reserva-notas-input" value={form.notas} rows={3} maxLength={500}
                  onChange={(e) => setForm({ ...form, notas: e.target.value })}
                  placeholder="Decoração, fornecedores, necessidades especiais…"
                  className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
              </div>
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 text-sm space-y-1" data-testid="reserva-summary">
                <p className="text-zinc-100">{area ? `${area.code} — ${area.name}` : ""}</p>
                <p className="text-zinc-400 text-xs">{fmtDataBR(form.data)} · {form.inicio}–{form.fim} · {form.solicitante_nome}</p>
                <p className="text-zinc-400 text-xs">Taxa de uso: <span className="text-zinc-100 font-medium">{fmtBRL(area?.taxa_uso)}</span></p>
              </div>
              {!editing && (
                <label className="flex items-start gap-2 text-sm text-zinc-300 cursor-pointer" data-testid="reserva-termos-label">
                  <Checkbox data-testid="reserva-termos-checkbox" checked={form.termos}
                    onCheckedChange={(v) => setForm({ ...form, termos: !!v })} className="mt-0.5" />
                  Confirmo que o solicitante leu e aceita as regras e os termos de uso do espaço. *
                </label>
              )}
            </div>
          )}

          <div className="flex justify-between gap-2 pt-2">
            <Button type="button" data-testid="reserva-back-button" variant="ghost"
              onClick={() => (step === 1 ? onOpenChange(false) : setStep(step - 1))}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
              <ChevronLeft className="h-4 w-4 mr-1" /> {step === 1 ? "Cancelar" : "Voltar"}
            </Button>
            {step < 3 ? (
              <Button type="button" data-testid="reserva-next-button" onClick={next}
                className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
                Avançar <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button type="button" data-testid="reserva-save-button" onClick={submit} disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Salvar alterações" : "Confirmar reserva"}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={eqlOpen} onOpenChange={setEqlOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="reserva-eql-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Selecionar EQL</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">Busque a unidade do solicitante na base Brave Access.</DialogDescription>
          </DialogHeader>
          <SearchableSelect onSelect={selectEql} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!diag} onOpenChange={(v) => !v && setDiag(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="reserva-moradores-diag-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">{diag?.titulo}</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Detalhes técnicos da consulta — copie e envie à equipe Brave Access. Você também pode preencher o solicitante manualmente.
            </DialogDescription>
          </DialogHeader>
          <pre data-testid="reserva-moradores-diag-text"
            className="text-xs bg-zinc-950 border border-zinc-800 rounded-md p-3 overflow-auto max-h-64 whitespace-pre-wrap text-zinc-300">{diag?.texto}</pre>
          <div className="flex justify-end">
            <Button type="button" data-testid="reserva-moradores-diag-close" variant="ghost" onClick={() => setDiag(null)}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">Fechar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
